import { FC, ReactNode, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';
import { Card } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlags,
  PlaceHoldModal,
  RemoveHoldModal,
  RenewModal,
  SearchInstanceDto,
} from '@app/components';
import { RecordSummaryContext, SummaryAvailability } from '@app/contexts';
import { useQueryParams } from '@app/hooks';
import { itemStatusPanelButtonMessages } from '@app/translations';
import { BREAKPOINT, FEATURE, ItemStatusData } from '@app/constants';

import { RecordSummaryCard } from './RecordSummaryCard';
import { ItemStatusPanel } from './ItemStatusPanel';

export type SummaryPlacement =
  | 'results'
  | 'instanceDetails'
  | 'holdsAndCheckouts';

export interface RecordSummaryProps {
  instance: SearchInstanceDto;
  placement: SummaryPlacement;
  itemStatusData?: ItemStatusData;
  links?: ReactNode;
  availability?: Partial<SummaryAvailability>;
  isAvailabilityLoading?: boolean;
  availabilityLoadingMessage?: string;
  testId?: string;
  altText?: string;
  isRTACShown?: boolean;
  LCLinkResolverLink?: string;
}

export const RecordSummary: FC<RecordSummaryProps> = props => {
  const {
    placement,
    availability = { isRequestable: true },
    itemStatusData,
    isAvailabilityLoading,
    isRTACShown = true,
    LCLinkResolverLink,
  } = props;

  const isMobile = useMediaQuery(
    `(max-width: ${BREAKPOINT.SCREEN.TABLET_SM - 1}px)`
  );
  const { $t } = useIntl();
  const [isPlaceHoldModalOpen, setPlaceHoldModalOpen] = useState(false);
  const [isRemoveHoldModalOpen, setRemoveHoldModalOpen] = useState(false);
  const [isRenewHoldModalOpen, setRenewHoldModalOpen] = useState(false);
  const [removeHoldError, setRemoveHoldError] = useState({
    isError: false,
    errorMessage: '',
  });
  const { isPlaceHold } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isInstanceDetails = placement === 'instanceDetails';
  const isMobileInstanceDetails = isMobile && isInstanceDetails;
  const isHoldsAndCheckouts = placement === 'holdsAndCheckouts';
  const isMobileHoldsAndCheckouts = isMobile && isHoldsAndCheckouts;
  const isAvailabilityLoadingForTheFirstTime = Boolean(
    isAvailabilityLoading && !availability.status
  );
  const shouldOpenPlaceHoldModal = Boolean(isPlaceHold);
  const isRenewModal =
    itemStatusData?.actionButtonText ===
    $t(itemStatusPanelButtonMessages.renew);

  const onModalOpenFromItemStatusPanel = isRenewModal
    ? setRenewHoldModalOpen
    : setRemoveHoldModalOpen;

  const getItemStatusPanel = () => (
    <ItemStatusPanel
      itemStatusData={itemStatusData as ItemStatusData}
      isRemoveHoldError={removeHoldError.isError}
      errorMessage={removeHoldError.errorMessage}
      onModalOpen={onModalOpenFromItemStatusPanel}
      onErrorClose={() =>
        setRemoveHoldError({ isError: false, errorMessage: '' })
      }
    />
  );

  const handleRemoveHoldModalClose = useCallback(() => {
    setRemoveHoldModalOpen(false);
  }, [setRemoveHoldModalOpen]);

  const handleRenewHoldModalClose = useCallback(() => {
    setRenewHoldModalOpen(false);
  }, [setRenewHoldModalOpen]);

  const handlePlaceHoldModalClose = useCallback(() => {
    setPlaceHoldModalOpen(false);

    if (shouldOpenPlaceHoldModal) {
      navigate(location.pathname);
    }
  }, [location.pathname, navigate, shouldOpenPlaceHoldModal]);

  return (
    <RecordSummaryContext.Provider
      value={{
        isInstanceDetails,
        isMobileInstanceDetails,
        isHoldsAndCheckouts,
        isMobileHoldsAndCheckouts,
        isAvailabilityLoadingForTheFirstTime,
        getItemStatusPanel,
        setPlaceHoldModalOpen,
        LCLinkResolverLink,
        ...props,
      }}
    >
      <Card border="bottom">
        <RecordSummaryCard isRTACShown={isRTACShown} />
        <FeatureFlags
          flagsToMatch={[{ name: FEATURE.placeHold, isActiveIfNotFound: true }]}
        >
          <PlaceHoldModal
            isOpen={isPlaceHoldModalOpen || shouldOpenPlaceHoldModal}
            onModalClose={handlePlaceHoldModalClose}
          />
        </FeatureFlags>
        {itemStatusData?.itemId && (
          <RenewModal
            isOpen={isRenewHoldModalOpen}
            onModalClose={handleRenewHoldModalClose}
          />
        )}
        {itemStatusData?.holdId && (
          <RemoveHoldModal
            isOpen={isRemoveHoldModalOpen}
            recordId={itemStatusData.holdId}
            onModalClose={handleRemoveHoldModalClose}
            onError={setRemoveHoldError}
          />
        )}
      </Card>
    </RecordSummaryContext.Provider>
  );
};
